import { buildPagedListPath } from "../../utils/listFilterQuery";
import {
  COMMISSION_TYPE_VALUES,
  type CommissionType,
} from "../../utils/propertyCommission";

export const PROPERTY_LIST_STATUS_FILTERS = [
  { value: "", label: "Todos" },
  { value: "available", label: "Ativo" },
  { value: "rented", label: "Inativo" },
  { value: "pre_registered", label: "Pré-cadastro" },
] as const;

export type PropertyListStatusFilter =
  (typeof PROPERTY_LIST_STATUS_FILTERS)[number]["value"];

export type PropertyListFurnishedFilter = "" | "0" | "1" | "2";

export type PropertyListCommissionFilter = "" | CommissionType;

export type PropertyListBooleanFlag = "" | "true" | "false";

export type PropertyListAdvancedFilters = {
  city: string;
  status: PropertyListStatusFilter;
  minRentValue: string;
  maxRentValue: string;
  condominium: string;
  isFurnished: PropertyListFurnishedFilter;
  commissionType: PropertyListCommissionFilter;
  rooms: string;
  bathrooms: string;
  garages: string;
  minArea: string;
  maxArea: string;
  consultantId: string;
};

export type PropertyListFilters = PropertyListAdvancedFilters & {
  search: string;
};

export function emptyPropertyListAdvancedFilters(): PropertyListAdvancedFilters {
  return {
    city: "",
    status: "",
    minRentValue: "",
    maxRentValue: "",
    condominium: "",
    isFurnished: "",
    commissionType: "",
    rooms: "",
    bathrooms: "",
    garages: "",
    minArea: "",
    maxArea: "",
    consultantId: "",
  };
}

export function emptyPropertyListFilters(): PropertyListFilters {
  return {
    search: "",
    ...emptyPropertyListAdvancedFilters(),
  };
}

export function parsePropertyListStatusFilter(
  value: string | null | undefined
): PropertyListStatusFilter {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();

  if (raw === "available" || raw === "active") return "available";
  if (raw === "rented" || raw === "inactive") return "rented";
  if (raw === "pre_registered") return "pre_registered";
  return "";
}

export function parseRentValueFilter(
  value: string | null | undefined
): string {
  if (value == null) return "";
  const digits = String(value).trim().replace(/[^\d]/g, "");
  if (digits === "") return "";
  const asNumber = Number(digits);
  if (!Number.isFinite(asNumber) || asNumber < 0) return "";
  return String(asNumber);
}

export function isInvalidMoneyRange(
  minValue: string,
  maxValue: string
): boolean {
  const min = parseRentValueFilter(minValue);
  const max = parseRentValueFilter(maxValue);
  if (min === "" || max === "") return false;
  return Number(max) < Number(min);
}

export function isInvalidRentRange(
  minRentValue: string,
  maxRentValue: string
): boolean {
  return isInvalidMoneyRange(minRentValue, maxRentValue);
}

export function parseCommissionTypeFilter(
  value: string | null | undefined
): PropertyListCommissionFilter {
  const raw = String(value ?? "").trim();
  if ((COMMISSION_TYPE_VALUES as readonly string[]).includes(raw)) {
    return raw as CommissionType;
  }
  return "";
}

export function parseFurnishedFilter(
  value: string | null | undefined
): PropertyListFurnishedFilter {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();

  if (raw === "true" || raw === "1") return "1";
  if (raw === "false" || raw === "0") return "0";
  if (raw === "2") return "2";
  return "";
}

export function parseIsRentedFilter(
  value: string | null | undefined
): PropertyListBooleanFlag {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();

  if (raw === "true" || raw === "1") return "true";
  if (raw === "false" || raw === "0") return "false";
  return "";
}

export function parseExactCountFilter(
  value: string | null | undefined,
  options?: { min?: number }
): string {
  const trimmed = String(value ?? "").trim();
  if (!/^\d+$/.test(trimmed)) return "";
  const count = Number(trimmed);
  const min = options?.min ?? 0;
  if (!Number.isFinite(count) || count < min) return "";
  return String(count);
}

export function occupancyStatusFromIsRented(
  isRented: PropertyListBooleanFlag
): PropertyListStatusFilter {
  if (isRented === "true") return "rented";
  if (isRented === "false") return "available";
  return "";
}

export function parseConsultantIdFilter(
  value: string | null | undefined
): string {
  const trimmed = String(value ?? "").trim();
  if (!/^\d+$/.test(trimmed)) return "";
  const id = Number(trimmed);
  if (!Number.isFinite(id) || id <= 0) return "";
  return String(id);
}

export function parseCityFilter(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

export function parseCondominiumFilter(
  value: string | null | undefined
): string {
  return String(value ?? "").trim();
}

function withFilledParam(
  key: string,
  value: string
): Record<string, string> {
  return value === "" ? {} : { [key]: value };
}

export function parsePropertyListFiltersFromSearch(
  search: string,
  options?: { includeConsultant?: boolean }
): PropertyListFilters {
  // isRented wins over status aliases so the UI never sends both with opposite
  // meaning. Occupancy is persisted as status (available | rented |
  // pre_registered), which also covers drafts.
  const query = new URLSearchParams(search);
  const includeConsultant = options?.includeConsultant !== false;

  const occupancyFromIsRented = occupancyStatusFromIsRented(
    parseIsRentedFilter(query.get("isRented"))
  );

  return {
    search: (query.get("search") ?? "").trim(),
    city: parseCityFilter(query.get("city")),
    status:
      occupancyFromIsRented ||
      parsePropertyListStatusFilter(query.get("status")),
    minRentValue: parseRentValueFilter(query.get("minRentValue")),
    maxRentValue: parseRentValueFilter(query.get("maxRentValue")),
    condominium: parseCondominiumFilter(query.get("condominium")),
    isFurnished: parseFurnishedFilter(query.get("isFurnished")),
    commissionType: parseCommissionTypeFilter(query.get("commissionType")),
    rooms: parseExactCountFilter(query.get("rooms"), { min: 1 }),
    bathrooms: parseExactCountFilter(query.get("bathrooms"), { min: 1 }),
    garages: parseExactCountFilter(query.get("garages")),
    minArea: parseRentValueFilter(query.get("minArea")),
    maxArea: parseRentValueFilter(query.get("maxArea")),
    consultantId: includeConsultant
      ? parseConsultantIdFilter(query.get("consultantId"))
      : "",
  };
}

export function pickAdvancedPropertyListFilters(
  filters: PropertyListFilters
): PropertyListAdvancedFilters {
  return {
    city: filters.city,
    status: filters.status,
    minRentValue: filters.minRentValue,
    maxRentValue: filters.maxRentValue,
    condominium: filters.condominium,
    isFurnished: filters.isFurnished,
    commissionType: filters.commissionType,
    rooms: filters.rooms,
    bathrooms: filters.bathrooms,
    garages: filters.garages,
    minArea: filters.minArea,
    maxArea: filters.maxArea,
    consultantId: filters.consultantId,
  };
}

export function countAdvancedPropertyListFilters(
  filters: PropertyListAdvancedFilters
): number {
  return [
    filters.city,
    filters.status,
    filters.minRentValue,
    filters.maxRentValue,
    filters.condominium,
    filters.isFurnished,
    filters.commissionType,
    filters.rooms,
    filters.bathrooms,
    filters.garages,
    filters.minArea,
    filters.maxArea,
    filters.consultantId,
  ].filter((value) => String(value).trim() !== "").length;
}

export function hasActivePropertyListFilters(
  filters: PropertyListFilters
): boolean {
  return Boolean(
    filters.search || countAdvancedPropertyListFilters(filters) > 0
  );
}

export function sanitizePropertyListAdvancedFilters(
  filters: PropertyListAdvancedFilters
): PropertyListAdvancedFilters {
  return {
    city: parseCityFilter(filters.city),
    status: parsePropertyListStatusFilter(filters.status),
    minRentValue: parseRentValueFilter(filters.minRentValue),
    maxRentValue: parseRentValueFilter(filters.maxRentValue),
    condominium: parseCondominiumFilter(filters.condominium),
    isFurnished: parseFurnishedFilter(filters.isFurnished),
    commissionType: parseCommissionTypeFilter(filters.commissionType),
    rooms: parseExactCountFilter(filters.rooms, { min: 1 }),
    bathrooms: parseExactCountFilter(filters.bathrooms, { min: 1 }),
    garages: parseExactCountFilter(filters.garages),
    minArea: parseRentValueFilter(filters.minArea),
    maxArea: parseRentValueFilter(filters.maxArea),
    consultantId: parseConsultantIdFilter(filters.consultantId),
  };
}

export function buildPropertiesPath(
  page: number,
  filters: PropertyListFilters
): string {
  return buildPagedListPath("/properties", page, {
    search: filters.search,
    city: filters.city,
    status: filters.status,
    minRentValue: filters.minRentValue,
    maxRentValue: filters.maxRentValue,
    condominium: filters.condominium,
    isFurnished: filters.isFurnished,
    commissionType: filters.commissionType,
    rooms: filters.rooms,
    bathrooms: filters.bathrooms,
    garages: filters.garages,
    minArea: filters.minArea,
    maxArea: filters.maxArea,
    consultantId: filters.consultantId,
  });
}

export function buildPropertiesApiParams(
  page: number,
  filters: PropertyListFilters
): Record<string, string | number> {
  return {
    page,
    ...withFilledParam("search", filters.search),
    ...withFilledParam("city", filters.city),
    ...withFilledParam("status", filters.status),
    ...withFilledParam("minRentValue", filters.minRentValue),
    ...withFilledParam("maxRentValue", filters.maxRentValue),
    ...withFilledParam("condominium", filters.condominium),
    ...withFilledParam("isFurnished", filters.isFurnished),
    ...withFilledParam("commissionType", filters.commissionType),
    ...withFilledParam("rooms", filters.rooms),
    ...withFilledParam("bathrooms", filters.bathrooms),
    ...withFilledParam("garages", filters.garages),
    ...withFilledParam("minArea", filters.minArea),
    ...withFilledParam("maxArea", filters.maxArea),
    ...withFilledParam("consultantId", filters.consultantId),
  };
}
